import { setUser, setTag } from '@sentry/vue';
import { useTenantStore } from '../stores/useTenantStore';

/**
 * quick-260629-lm6 — Sentry user identity + tenant context.
 *
 * Legacy Vue 2 consumer-portal set only `Sentry.setUser({ email })` at init;
 * the current customer-portal sends ZERO user context. This module closes that
 * gap and exceeds parity: it sets `{ id, email, username?, role? }` on the
 * Sentry user and a global `tenant` tag so triage can attribute every error /
 * replay to a real customer on a specific tenant.
 *
 * Dependency-direction decision (the main one for this task): `libs/core` is the
 * leaf — `apps/customer-portal` depends on `@sera/core`, never the reverse. The
 * auth store lives HERE in libs/core and therefore CANNOT import the app's
 * `plugins/sentry.ts`. `@sentry/vue` IS a root dependency reachable by libs/core
 * and its `setUser`/`setTag` operate on the global Sentry hub regardless of
 * which module calls them (and which module called `Sentry.init`). So these
 * helpers live here (the one place the store can reach), the auth store imports
 * them from this module (never `@sentry/vue` directly), and the app's
 * `plugins/sentry.ts` re-exports them so the "all @sentry/vue imports
 * centralized at the app layer" intent is preserved for app-side callers.
 *
 * The helpers are safe to call when Sentry is disabled (localhost/dev) — the SDK
 * no-ops on an uninitialized hub.
 */
export interface SentryUser {
  id: number | string;
  email: string;
  username?: string;
  role?: string;
}

/**
 * Identify the current customer on the global Sentry hub and stamp the tenant
 * tag. Only DEFINED fields are sent: `id` and `email` always; `username` ONLY
 * when it is a non-empty string (so an absent name is a MISSING KEY, not
 * `username: undefined`, which Sentry would serialize); `role` when present.
 *
 * The tenant slug is read off the tenant store the same way identifyPendo does
 * (`config?.tenantSlug ?? 'unknown'`) — no hostname re-parse.
 */
export function setSentryUser(u: SentryUser): void {
  const userObj: Record<string, number | string> = {
    id: u.id,
    email: u.email,
  };
  if (typeof u.username === 'string' && u.username.length > 0) {
    userObj.username = u.username;
  }
  if (u.role != null) {
    userObj.role = u.role;
  }
  setUser(userObj);

  const tenantStore = useTenantStore();
  const tenantSlug = tenantStore.config?.tenantSlug ?? 'unknown';
  setTag('tenant', tenantSlug);
}

/**
 * Clear the Sentry user and the global tenant tag on logout. `setUser(null)`
 * detaches the user; `setTag('tenant', null)` clears the tag.
 */
export function clearSentryUser(): void {
  setUser(null);
  setTag('tenant', null);
}
